import { Component, type ErrorInfo, type ReactNode } from 'react';
import { sentryCaptureException } from '../../lib/sentry.js';
import './error-boundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * ErrorBoundary — red de seguridad en la raíz de la app: si cualquier render
 * crashea, en vez de pantalla blanca (o una tab colgada que el usuario no
 * puede recuperar) mostramos un fallback mínimo con botón de recarga.
 *
 * El crash se loguea con tag `[error-boundary]` + componentStack: en dev Vite
 * reenvía la consola del browser al log del dev server (PM2), así que el
 * stack queda capturado server-side sin pedirle nada al usuario.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[error-boundary] render crash', error, info.componentStack);
    // A caught render error never reaches the global handlers: report it here, or
    // a screen that crashes is invisible to the OTA rollout's health check.
    sentryCaptureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
    });
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="error-boundary" role="alert">
        <svg
          className="error-boundary-orb"
          viewBox="0 0 240 240"
          width={48}
          height={48}
          aria-hidden
          role="img"
        >
          <circle cx="120" cy="120" r="74" fill="none" stroke="var(--purple)" strokeWidth="16" />
          <circle cx="190" cy="76" r="22" fill="var(--lime)" />
        </svg>
        <h1>Algo salió mal</h1>
        <p>La app encontró un error inesperado. Recargala para seguir donde estabas.</p>
        <button type="button" onClick={() => window.location.reload()}>
          Recargar
        </button>
      </div>
    );
  }
}
